import { Component, Input } from "@angular/core";
import { ButtonComponent } from "@evc/web-components";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Observable, of, throwError, zip } from "rxjs";
import { switchMap } from "rxjs/operators";
import { ApplicationError } from "../../_errors/application-error";
import { FILE_SYSTEM, OFFLINE_REGEX } from "../../_offline/electron.constants";
import { SubjectModelSchema } from "../../_offline/models/subjectModel.schema";
import { OfflineUtilsService } from "../../_offline/services/offline-utils.service";
import { Inspection } from "../../_objects/inspection";
import { SubjectLocation } from "../../_objects/subject-model";
import { CommonSnackBarService } from "../../_services/common-snack-bar.service";
import { SubjectModelDbService } from "../../_services/database/subject-model-db.service";
import { TitleDbService } from "../../_services/database/title-db.service";
import { FileSystemAccessService } from "../../_services/file-system-access.service";
import { MarksStateService } from "../../_services/mark-state/marks-state.service";
import { SpinnerService } from "../../_services/spinner.service";
import { UtilsService } from "../../_services/_utils.service";
import { TenantConfig } from "../../app.constants";
import { NetworkStatusService } from "../../_services/network-status.service";
import { IplexDeviceSyncService } from "../../_offline/services/iplex-device-sync.service";
import { IplexDeviceConnectModeService } from "../../_offline/services/iplex-device-connect-mode.service";

@Component({
  selector: "app-send-to-iplex-g",
  imports: [TranslateModule, ButtonComponent],
  templateUrl: "./send-to-iplex-g.component.html",
  styleUrl: "./send-to-iplex-g.component.scss"
})
export class SendToIplexGComponent {
  @Input() inspection: Inspection;

  markLabel = TenantConfig.DefaultConfigurationValues.LabelOfMark;
  titleLabel = TenantConfig.DefaultConfigurationValues.LabelOfTitle;
  private folderIndex = 1;
  invalidLocationFolderNameList: string[] = [];

  constructor(
    private translateService: TranslateService,
    private spinnerService: SpinnerService,
    private snackBarService: CommonSnackBarService,
    private titleDBService: TitleDbService,
    private marksStateService: MarksStateService,
    private subjectModelDbService: SubjectModelDbService,
    private iplexGSyncService: IplexDeviceSyncService,
    private fileSystemAccessService: FileSystemAccessService,
    private offlineUtils: OfflineUtilsService,
    private utilsService: UtilsService,
    private networkStatusService: NetworkStatusService,
    private deviceConnectModeService: IplexDeviceConnectModeService
  ) {}

  public get isBrianMode() {
    return this.networkStatusService.isBrianMode;
  }

  get isOffline() {
    return this.networkStatusService.isOffline;
  }

  get isDeviceConnectMode() {
    return this.isOffline && !this.isBrianMode && this.deviceConnectModeService.isHttpsConnectMode;
  }

  get lowerCaseMarkLabel(): string {
    return this.markLabel.toLowerCase();
  }

  get lowerCaseTitleLabel(): string {
    return this.titleLabel.toLowerCase();
  }

  sendDataForIPLEXG() {
    const titleSrcPath = `${FILE_SYSTEM.PATH.PRESET}\\TITLE.dat`;
    const marSrcPath = `${FILE_SYSTEM.PATH.PRESET}\\MARK_SET.dat`;
    const inspectionFolderName = this.utilsService.trimAllSpace(this.inspection.title);
    let subjectModelSchema: SubjectModelSchema = null;

    this.spinnerService.show();
    const createTitleFileOb = this.titleDBService
      .getAll()
      .pipe(switchMap((schema) => this.fileSystemAccessService.createTextFile(this.offlineUtils.generateTitlesText(schema), titleSrcPath)));

    const createMarkFileOb = this.marksStateService.markItems$.pipe(
      switchMap((markItems) => this.fileSystemAccessService.createTextFile(this.offlineUtils.generateMarksText(markItems), marSrcPath))
    );

    zip(this.subjectModelDbService.getOne(this.inspection.subjectModelId), createTitleFileOb, createMarkFileOb)
      .pipe(
        switchMap((results) => {
          subjectModelSchema = results[0];
          const isTitleFileSuccess = results[1];
          const isMarkFileSuccess = results[2];

          if (!isTitleFileSuccess || !isMarkFileSuccess) {
            throw new ApplicationError("Title/Mark data is invalid");
          }

          return this.iplexGSyncService.onWebDAVConnectMode();
        })
      )
      .pipe(
        switchMap((isSuccess) => {
          if (!isSuccess) {
            return throwError("Failed link with IPLEX G.");
          }

          return this.postDatFiles();
        })
      )
      .pipe(
        switchMap((isSuccess) => {
          if (isSuccess.some((_) => _ === false)) {
            throwError({ type: "postDatFiles" });
          }

          let postInspectionFolderOb: Observable<boolean> = of(false);
          if (OFFLINE_REGEX.File_Stat_Basename.test(inspectionFolderName)) {
            postInspectionFolderOb = this.iplexGSyncService.postDirectoryInSDcard(`/${inspectionFolderName}`);
          }

          return postInspectionFolderOb;
        })
      )
      .pipe(
        switchMap((isSuccess) => {
          if (!isSuccess) {
            throwError({ type: "failedInspectionFolder" });
          }
          const subjectModelLocation = subjectModelSchema.subjectModelLocations.find((_) => _.version === this.inspection.locationVersion);
          const locations = JSON.parse(subjectModelLocation.locations) as SubjectLocation[];
          const filteredLocations = locations.filter((location) => OFFLINE_REGEX.File_Stat_Basename.test(location.identifier));

          return this.postSubjectLocationFolders(inspectionFolderName, filteredLocations);
        })
      )
      .pipe(
        switchMap((isSuccess: boolean[]) => {
          if (isSuccess.some((_) => _ === false)) {
            throwError({ type: "failedInspectionFolder" });
          }
          return this.iplexGSyncService.stopSdCardWebDAV();
        })
      )
      .subscribe(
        (_) => {
          this.snackBarService.open(this.translateService.instant("inspectionUpsertDialog.sendSuccess"));
          this.spinnerService.hide();
        },
        (error) => {
          console.log(error);
          if (error?.type === "postDatFiles") {
            this.snackBarService.open(
              this.translateService.instant("inspectionUpsertDialog.sendDatFilesFailed", {
                lowerCaseMarkLabel: this.lowerCaseMarkLabel,
                lowerCaseTitleLabel: this.lowerCaseTitleLabel
              })
            );
          } else if (error?.type === "failedInspectionFolder") {
            this.snackBarService.open(this.translateService.instant("inspectionUpsertDialog.sendInspectionFolderFailed"));
          } else {
            this.snackBarService.open(this.translateService.instant("inspectionUpsertDialog.sendFailed"));
          }
          this.spinnerService.hide();
          this.iplexGSyncService.offWebDAVConnectMode().subscribe((_) => _);
        }
      );
  }

  postDatFiles() {
    const presetFolderPath = `/${FILE_SYSTEM.PATH.PRESET}`;
    const titleSrcPath = `${FILE_SYSTEM.PATH.PRESET}\\TITLE.dat`;
    const titleDestPath = `/${FILE_SYSTEM.PATH.PRESET}/TITLE.dat`;
    const marSrcPath = `${FILE_SYSTEM.PATH.PRESET}\\MARK_SET.dat`;
    const marDestPath = `/${FILE_SYSTEM.PATH.PRESET}/MARK_SET.dat`;

    return this.iplexGSyncService.postDirectoryInSDcard(presetFolderPath).pipe(
      switchMap((isSuccess) => {
        return isSuccess
          ? zip(
              this.iplexGSyncService.postFileForSDcard(titleSrcPath, titleDestPath),
              this.iplexGSyncService.postFileForSDcard(marSrcPath, marDestPath)
            )
          : of([false, false]);
      })
    );
  }

  postSubjectLocationFolders(inspectionName: string, locations: SubjectLocation[]) {
    this.folderIndex = 1;
    const folderList: string[] = [];
    this.invalidLocationFolderNameList = [];
    locations.forEach((location) => this.addLocationFolder(location, folderList));

    const obs = folderList
      .map((folderName) => `/${inspectionName}/${folderName}`)
      .map((folderPath) => this.iplexGSyncService.postDirectoryInSDcard(folderPath));

    return zip(...obs);
  }

  private addLocationFolder(location: SubjectLocation, folderList: string[]) {
    if (location.children.length > 0) {
      location.children.forEach((child) => {
        this.addLocationFolder(child, folderList);
      });
    } else {
      if (location.isDataPlate) {
        const folderName = "00_" + location.identifier;
        if (OFFLINE_REGEX.File_Stat_Basename.test(folderName)) {
          folderList.push(folderName);
        } else {
          this.invalidLocationFolderNameList.push(location.identifier);
        }
      } else {
        const folderName = String(this.folderIndex).padStart(2, "0") + "_" + location.identifier;
        if (OFFLINE_REGEX.File_Stat_Basename.test(folderName)) {
          folderList.push(folderName);
        } else {
          this.invalidLocationFolderNameList.push(location.identifier);
        }
        this.folderIndex++;
      }
    }

    return folderList;
  }
}
